'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Search, Users, Star, Guitar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/auth-context';
import { useAdmin } from '@/hooks/use-admin';
import { useToast } from '@/hooks/use-toast';
import { getTalentByRole, getIncomingBookings, getAllBookings, updateBookingStatus } from '@/services/booking';
import { TALENT_ROLES } from '@/types/booking';
import type { BookingProfile, BookingRequest, TalentRoleId } from '@/types/booking';
import { TalentCard } from '@/components/booking/talent-card';
import { BookTalentDialog } from '@/components/booking/book-talent-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// ─── Skeleton card ────────────────────────────────────────────────────────────
function TalentCardSkeleton() {
  return (
    <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-muted shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-3 w-20 bg-muted rounded" />
        </div>
      </div>
      <div className="flex gap-1">
        <div className="h-5 w-16 bg-muted rounded" />
        <div className="h-5 w-14 bg-muted rounded" />
      </div>
      <div className="h-8 w-20 bg-muted rounded ml-auto" />
    </div>
  );
}

// ─── Role tab icon map (Lucide doesn't export all icons so we use text labels) ─
function RoleTabLabel({ icon, label }: { icon: string; label: string }) {
  // icon is a string key — just show label text since we store string keys in TALENT_ROLES
  return <span>{label}</span>;
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function BookPage() {
  const { user } = useAuth();
  const { isAdmin } = useAdmin();

  const [view, setView] = useState<'directory' | 'bookings'>('directory');
  const [activeRole, setActiveRole] = useState<TalentRoleId>('all');
  const [talent, setTalent] = useState<BookingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bookingTarget, setBookingTarget] = useState<BookingProfile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookings, setBookings] = useState<BookingRequest[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingRequest | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const { toast } = useToast();
  const ti = useTranslations('instruments');
  const tb = useTranslations('bookTalent');
  // Musicians are booked by what they play (Gordon 2026-11-03).
  const [instrument, setInstrument] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Directory: super admins see ALL creators (so it's never empty just because
  // nobody toggled "available"); everyone else sees only opted-in talent.
  useEffect(() => {
    if (view !== 'directory') return;
    let cancelled = false;
    setLoading(true);
    const roleEntry = TALENT_ROLES.find(r => r.id === activeRole);
    const firestoreRole = roleEntry?.firestoreRole ?? null;
    getTalentByRole(firestoreRole, isAdmin ? 200 : 50, isAdmin).then(results => {
      if (!cancelled) {
        setTalent(results);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [activeRole, view, isAdmin]);
  useEffect(() => { setInstrument(null); }, [activeRole]);

  // The instruments the loaded talent play, most common first.
  const instrumentOptions = useMemo(() => {
    const count = new Map<string, number>();
    talent.forEach(p => (p.instruments ?? []).forEach(i => count.set(i, (count.get(i) ?? 0) + 1)));
    return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name]) => name);
  }, [talent]);

  // Bookings: super admin sees ALL requests; a creator sees only requests
  // addressed to them ("what regards them").
  useEffect(() => {
    if (view !== 'bookings' || !user?.uid) return;
    let cancelled = false;
    setBookingsLoading(true);
    const load = isAdmin ? getAllBookings() : getIncomingBookings(user.uid);
    load.then(rows => {
      if (!cancelled) {
        setBookings(rows);
        setBookingsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [view, isAdmin, user?.uid]);

  // A booking's chat card / coπ opens /book?booking=<id>: show that request (Gordon 2026-11-03).
  const openBookingId = useRef<string | null>(null);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('booking');
    if (id) { openBookingId.current = id; setView('bookings'); }
  }, []);
  useEffect(() => {
    const id = openBookingId.current;
    if (!id || bookingsLoading) return;
    const hit = bookings.find(b => b.id === id);
    if (hit) { setSelectedBooking(hit); openBookingId.current = null; }
  }, [bookings, bookingsLoading]);

  const filtered = talent.filter(t => {
    if (instrument && !(t.instruments ?? []).includes(instrument)) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      t.displayName.toLowerCase().includes(s) ||
      (t.bookingCity ?? '').toLowerCase().includes(s) ||
      (t.instruments ?? []).some(i => i.toLowerCase().includes(s))
    );
  });

  function handleBook(t: BookingProfile) {
    setBookingTarget(t);
    setDialogOpen(true);
  }

  /** Accept / decline / mark-reviewed from the booking detail dialog. */
  async function setBookingStatus(b: BookingRequest, status: BookingRequest['status']) {
    // Legacy requests may lack talentId on the doc — for a creator viewing
    // their own incoming requests the subcollection owner IS them.
    const talentId = b.talentId || (!isAdmin ? user?.uid : '') || '';
    if (!b.id || !talentId) {
      toast({ title: 'Cannot update this booking', description: 'Missing booking reference.', variant: 'destructive' });
      return;
    }
    setStatusSaving(true);
    try {
      await updateBookingStatus(talentId, b.id, status);
      setBookings(prev => prev.map(x => (x.id === b.id ? { ...x, status } : x)));
      setSelectedBooking(prev => (prev && prev.id === b.id ? { ...prev, status } : prev));
      toast({ title: `Booking ${status}`, description: `${b.clientName}'s request is now ${status}.` });
    } catch (err: any) {
      toast({ title: 'Update failed', description: err?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setStatusSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Page header */}
      <div className="px-page pt-8 pb-6 border-b border-border">
        <h1 className="text-2xl font-bold text-foreground">Book Talent</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Find and hire music professionals
        </p>
      </div>

      {/* View toggle — logged-in users get a Bookings view scoped to their role.
          Bookings first (per Gordon), Browse Talent second. */}
      {user && (
        <div className="flex gap-2 px-page pt-4">
          {(['bookings', 'directory'] as const).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn(
                'px-3 py-1.5 rounded-[var(--btn-radius)] text-sm font-medium border transition-colors',
                view === v
                  ? 'bg-btn-selected text-btn-selected-foreground border-btn-selected'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground hover:bg-muted/50'
              )}
            >
              {v === 'directory' ? 'Browse Talent' : isAdmin ? 'All Bookings' : 'My Bookings'}
            </button>
          ))}
        </div>
      )}

      {view === 'directory' && (<>
      {/* Role tabs */}
      <div
        ref={scrollRef}
        className="flex overflow-x-auto hide-scrollbar gap-2 px-page py-3 border-b border-border"
      >
        {TALENT_ROLES.map(role => (
          <button
            key={role.id}
            onClick={() => setActiveRole(role.id)}
            className={cn(
              'shrink-0 px-3 py-1.5 rounded-[var(--btn-radius)] text-sm font-medium transition-colors whitespace-nowrap border',
              activeRole === role.id
                ? 'bg-btn-selected text-btn-selected-foreground border-btn-selected'
                : 'bg-transparent text-muted-foreground border-border hover:text-foreground hover:bg-muted/50'
            )}
          >
            <RoleTabLabel icon={role.icon} label={role.label} />
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-page py-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or city..."
            className="pl-9"
          />
        </div>
      </div>

      {/* Instrument filter — only when someone in this category plays something */}
      {instrumentOptions.length > 0 && (
        <div role="group" aria-label={ti('filter')} className="flex items-center gap-2 overflow-x-auto hide-scrollbar px-page pb-4">
          <Guitar className="h-4 w-4 shrink-0" aria-hidden />
          {[null, ...instrumentOptions].map(name => (
            <button
              key={name ?? 'all'}
              type="button"
              aria-pressed={instrument === name}
              onClick={() => setInstrument(name)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-[var(--btn-radius)] border px-3 py-1 text-xs font-semibold transition-colors',
                instrument === name
                  ? 'bg-btn-selected text-btn-selected-foreground border-btn-selected'
                  : 'bg-transparent text-foreground border-border hover:bg-muted/50'
              )}
            >
              {name ?? ti('all')}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="px-page pb-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <TalentCardSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
              <Users className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-foreground">No talent found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {search
                  ? 'Try a different search term or browse another category.'
                  : 'No one has enabled booking in this category yet.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(t => (
              <TalentCard key={t.userId} talent={t} onBook={handleBook} />
            ))}
          </div>
        )}
      </div>
      </>)}

      {/* Bookings view — scoped to role */}
      {view === 'bookings' && (
        <div className="px-page py-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'All booking requests across the platform.' : 'Booking requests sent to you.'}
          </p>
          {bookingsLoading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 rounded-lg bg-muted/40 animate-pulse" />)}</div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center"><Users className="h-7 w-7 text-muted-foreground" /></div>
              <p className="font-semibold text-foreground">No booking requests yet</p>
              <p className="text-sm text-muted-foreground max-w-sm">
                {isAdmin
                  ? 'When clients send booking enquiries to any creator, they appear here.'
                  : 'When someone wants to book you, their request shows up here. Make sure you’ve enabled “Available for booking” in your profile.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setSelectedBooking(b)}
                  className="w-full text-left rounded-lg border border-border bg-card p-4 transition-colors hover:border-foreground/30 hover:bg-muted/40 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {b.clientName}
                        {isAdmin && b.talentName && <span className="text-muted-foreground font-normal"> → {b.talentName}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[b.eventType, b.location, b.proposedDate].filter(Boolean).join(' · ') || 'Booking enquiry'}
                        {b.proposedFee ? ` · ${b.proposedFee} ${(b.currency || '').toUpperCase()}` : ''}
                      </p>
                      {b.message && <p className="text-sm text-foreground/80 mt-2 line-clamp-3">{b.message}</p>}
                      {(b.rating || 0) > 0 && (
                        <span className="inline-flex items-center gap-0.5 mt-1.5">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star key={n} className={cn('h-3.5 w-3.5', (b.rating || 0) >= n ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30')} />
                          ))}
                          {b.review && <span className="text-xs text-muted-foreground ml-1.5 truncate max-w-[220px]">“{b.review}”</span>}
                        </span>
                      )}
                      {b.clientEmail && <p className="text-[11px] text-muted-foreground mt-1">{b.clientEmail}{b.clientPhone ? ` · ${b.clientPhone}` : ''}</p>}
                      {b.bbCustomerOptIn && <span className="mt-1 inline-block rounded-full bg-primary px-2 py-0.5 text-2xs font-semibold text-primary-foreground">{tb('optedIn')}</span>}
                    </div>
                    <span className={cn(
                      'text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 font-medium',
                      b.status === 'paid' ? 'bg-violet-600 text-white'
                        : b.status === 'accepted' ? 'bg-emerald-600 text-white'
                        : b.status === 'declined' ? 'bg-red-600 text-white'
                        : b.status === 'reviewed' ? 'bg-blue-600 text-white'
                        : 'bg-amber-500 text-black'
                    )}>{b.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Booking detail dialog — opened by clicking a request row */}
      <Dialog open={!!selectedBooking} onOpenChange={v => { if (!v) setSelectedBooking(null); }}>
        <DialogContent className="max-w-md">
          {selectedBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Booking request
                  <span className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full font-medium',
                    selectedBooking.status === 'paid' ? 'bg-violet-600 text-white'
                      : selectedBooking.status === 'accepted' ? 'bg-emerald-600 text-white'
                      : selectedBooking.status === 'declined' ? 'bg-red-600 text-white'
                      : selectedBooking.status === 'reviewed' ? 'bg-blue-600 text-white'
                      : 'bg-amber-500 text-black'
                  )}>{selectedBooking.status}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">From</p>
                  <p className="font-medium text-foreground">{selectedBooking.clientName || 'Unknown'}</p>
                  {selectedBooking.clientEmail && (
                    <a href={`mailto:${selectedBooking.clientEmail}`} className="text-xs text-primary underline underline-offset-2">
                      {selectedBooking.clientEmail}
                    </a>
                  )}
                  {selectedBooking.clientPhone && (
                    <a href={`tel:${selectedBooking.clientPhone.replace(/\s/g, '')}`} className="block text-xs text-primary underline underline-offset-2">
                      {selectedBooking.clientPhone}
                    </a>
                  )}
                  {selectedBooking.bbCustomerOptIn && (
                    <span className="mt-1 inline-block rounded-full bg-primary px-2 py-0.5 text-2xs font-semibold text-primary-foreground">{tb('optedIn')}</span>
                  )}
                </div>
                {isAdmin && selectedBooking.talentName && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">To</p>
                    <p className="font-medium text-foreground">{selectedBooking.talentName}</p>
                  </div>
                )}
                {(selectedBooking.eventType || selectedBooking.location || selectedBooking.proposedDate || selectedBooking.proposedFee) && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedBooking.eventType && (
                      <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event</p><p className="text-foreground">{selectedBooking.eventType}</p></div>
                    )}
                    {selectedBooking.location && (
                      <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Location</p><p className="text-foreground">{selectedBooking.location}</p></div>
                    )}
                    {selectedBooking.proposedDate && (
                      <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Date</p><p className="text-foreground">{selectedBooking.proposedDate}</p></div>
                    )}
                    {typeof selectedBooking.proposedFee === 'number' && selectedBooking.proposedFee > 0 && (
                      <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Proposed fee</p><p className="text-foreground">{selectedBooking.proposedFee} {(selectedBooking.currency || '').toUpperCase()}</p></div>
                    )}
                  </div>
                )}
                {selectedBooking.message && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Message</p>
                    <p className="text-foreground/90 whitespace-pre-wrap">{selectedBooking.message}</p>
                  </div>
                )}
                {(selectedBooking.rating || 0) > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Client rating</p>
                    <span className="inline-flex items-center gap-0.5 mt-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} className={cn('h-4 w-4', (selectedBooking.rating || 0) >= n ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30')} />
                      ))}
                    </span>
                    {selectedBooking.review && (
                      <p className="text-foreground/90 whitespace-pre-wrap mt-1">“{selectedBooking.review}”</p>
                    )}
                  </div>
                )}
              </div>

              {/* ── Payroll: accepted booking with a fee → shareable payment link.
                     The client pays on a public page; funds destination-charge to
                     the talent's Connect account (π% platform fee). ── */}
              {selectedBooking.status === 'accepted' && (selectedBooking.proposedFee || 0) > 0 && (
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Get paid</p>
                  <p className="text-sm text-foreground/80">
                    Send the client this payment link — {selectedBooking.proposedFee} {(selectedBooking.currency || '').toUpperCase()} goes to your connected payout account.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 text-white hover:bg-emerald-500"
                      onClick={() => {
                        const tid = selectedBooking.talentId || (!isAdmin ? user?.uid : '') || '';
                        const link = `${window.location.origin}/book/pay/${tid}/${selectedBooking.id}`;
                        navigator.clipboard?.writeText(link)
                          .then(() => toast({ title: 'Payment link copied', description: 'Paste it to the client — they can pay without signing in.' }))
                          .catch(() => toast({ title: link, description: 'Copy the link above manually.' }));
                      }}
                    >
                      Copy payment link
                    </Button>
                    {selectedBooking.clientEmail && (
                      <Button asChild size="sm" variant="outline">
                        <a href={`mailto:${selectedBooking.clientEmail}?subject=${encodeURIComponent('Payment for your booking — Breakin’ Bread')}&body=${encodeURIComponent(`Hi ${selectedBooking.clientName || ''},\n\nHere is the payment link for our booking (${selectedBooking.proposedFee} ${(selectedBooking.currency || '').toUpperCase()}):\n${typeof window !== 'undefined' ? window.location.origin : ''}/book/pay/${selectedBooking.talentId || (!isAdmin ? user?.uid : '') || ''}/${selectedBooking.id}\n\nThanks!`)}`}>
                          Email payment link
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {selectedBooking.status === 'paid' && (
                <div className="rounded-lg border border-emerald-600/40 bg-emerald-600/10 p-3 text-sm font-medium text-emerald-600">
                  ✓ Paid{selectedBooking.proposedFee ? ` — ${selectedBooking.proposedFee} ${(selectedBooking.currency || '').toUpperCase()}` : ''} · routed to your payout account
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                {selectedBooking.status !== 'paid' && (
                  <>
                    <Button
                      disabled={statusSaving || selectedBooking.status === 'accepted'}
                      onClick={() => setBookingStatus(selectedBooking, 'accepted')}
                      className="bg-emerald-600 text-white hover:bg-emerald-500"
                    >
                      Accept
                    </Button>
                    <Button
                      disabled={statusSaving || selectedBooking.status === 'declined'}
                      onClick={() => setBookingStatus(selectedBooking, 'declined')}
                      className="bg-red-600 text-white hover:bg-red-500"
                    >
                      Decline
                    </Button>
                  </>
                )}
                {selectedBooking.status === 'pending' && (
                  <Button
                    variant="outline"
                    disabled={statusSaving}
                    onClick={() => setBookingStatus(selectedBooking, 'reviewed')}
                  >
                    Mark reviewed
                  </Button>
                )}
                {selectedBooking.clientEmail && (
                  <Button asChild variant="outline">
                    <a href={`mailto:${selectedBooking.clientEmail}?subject=${encodeURIComponent('Re: your booking request on Breakin’ Bread')}`}>
                      Reply by email
                    </a>
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking dialog */}
      {bookingTarget && (
        <BookTalentDialog
          talent={bookingTarget}
          open={dialogOpen}
          onOpenChange={v => {
            setDialogOpen(v);
            if (!v) setBookingTarget(null);
          }}
          currentUser={
            user
              ? { uid: user.uid, displayName: user.displayName, email: user.email }
              : null
          }
        />
      )}
    </div>
  );
}
